import React from 'react'
import { useAtomValue } from 'jotai'
import dt from 'dayjs'
import Decimal from 'decimal.js'
import * as state from '../../states'
import {
	type TOfferSpendByOfferScope,
	type TOfferSpendByOfferScopeAggregated,
	EOfferSpendByOfferScopeView,
	TOfferSpendByOfferScopeDay,
	TOfferSpendByOfferScopeAggOnly,
	ERoleAccess
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	headCellFormatterSortable,
	ESortOrder,
	UiTotalLabel
} from '../ui'
import { OFFER_SPEND_BY_OFFER_SCOPE_PAGE_SORT_FIELDS, IOfferSpendByOfferScopeSortField } from '../../hooks'
import { Access } from '../../lib'
import { Day, Days, renderTotal, renderBy } from './OfferSpendByAccountListTable'
import * as helpers from '../../helpers'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferSpendByOfferScopeListTableProps = {
	viewBy: EOfferSpendByOfferScopeView
	range: Array<dt.Dayjs>
	items: TOfferSpendByOfferScope[];
	skip: number;
	sortBy?: IOfferSpendByOfferScopeSortField;
	setSortBy?: (sortBy: IOfferSpendByOfferScopeSortField) => void;
	order?: ESortOrder;
	setOrder?: (order: ESortOrder) => void;
	onViewOfferSpendDetails?: (item: TOfferSpendByOfferScopeDay | TOfferSpendByOfferScopeAggOnly, e: React.MouseEvent) => void;
}

export const OfferSpendByOfferScopeListTable = (props: IOfferSpendByOfferScopeListTableProps) => {
	const {
		viewBy,
		range,
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onViewOfferSpendDetails
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const tableConfig: IUiTableConfig<TOfferSpendByOfferScope | null>[] = [
		{
			key: 'scopeId',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_scope_list_table.column_scope_id),
			rowCellFormatter({ rowData }) {
				if (!rowData) {
					return ''
				}
				return rowData.scopeId
			}
		},
		{
			key: 'scope.name',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_scope_list_table.column_scope),
			classesToAdd: {
				th: 'border-end',
				td: 'border-end'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return <UiTotalLabel/>
				}

				const scope = rowData?.scope ?? null
				const name = scope?.name ?? null

				return <>{name ?? '?'}</>
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_scope_list_table.column_amount),
			classesToAdd: {
				th: 'text-center'
			},
			rowCellFormatter: ({ rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = rowsData
						.map(_ => _?.amount ?? 0)
						.reduce((sum, amount) => sum.add(amount), Decimal(0))
					return renderTotal(total, rowIndex, cellIndex)
				}
				const total = Decimal(rowData.amount)
				return renderTotal(total, rowIndex, cellIndex)
			}
		},
		...range.map((date: dt.Dayjs, index: number): IUiTableConfig<TOfferSpendByOfferScope | null> => {
			const day = viewBy === EOfferSpendByOfferScopeView.Month ? date.format('MM.YYYY') : date.format('DD.MM.YYYY')

			return {
				key: `agg-${day}-${index}`,
				title: `${day}`,
				headCellFormatter() {
					return <Days><Day>{day}</Day></Days>
				},
				rowCellFormatter: ({ rowData, rowsData, rowIndex, cellIndex }) => {
					const unit = viewBy
					const filterFunc = (item: TOfferSpendByOfferScopeAggregated) => {
						return dt.utc(item.at).startOf(unit).diff(date, unit) === 0
					}

					if (!rowData) {
						const agg = rowsData.map((rowData): TOfferSpendByOfferScopeAggregated[] => {
							const allAgg = rowData?.agg ?? []
							const agg = allAgg.filter(filterFunc)
							return agg
						})

						const aggFlat = agg.flat()
						const total = aggFlat
							.map(_ => _?.amount ?? 0)
							.reduce((sum, amount) => sum.add(amount), Decimal(0))
						return renderTotal(total, rowIndex, cellIndex)
					}
					const allAgg = rowData.agg
					const agg = allAgg.filter(filterFunc)
					return renderBy($access.$(ERoleAccess.OfferSpendByOfferScopeList), onViewOfferSpendDetails)(agg, rowData, rowIndex, cellIndex, index, viewBy, day)
				}
			}
		})
	]

	const tableConfigSortable: IUiTableConfig<TOfferSpendByOfferScope>[] = tableConfig.map(
		(configItem) => {
			const configItemIsSortable = OFFER_SPEND_BY_OFFER_SCOPE_PAGE_SORT_FIELDS.includes(configItem.key as IOfferSpendByOfferScopeSortField)

			if (!configItemIsSortable || !sortBy || !setSortBy || !order || !setOrder) { // ?!?!
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('OfferSpendByOfferScopeListTable:: ', { pendListTableConfig, roleListTableConfigSortable })
	const { dataItems, tableClassName } = helpers.getTableItemsWithTotalRows(items)

	return <>
		<UiTable
			data={dataItems}
			config={tableConfigSortable}
			// TODO: uncomment if necessary
			classesToAdd={{
				//wrapper: 'table-sticky-wrapper',
				table: tableClassName
			}}
		/>
	</>
}